import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FaTimes, 
  FaCreditCard, 
  FaEdit, 
  FaPlus, 
  FaDollarSign, 
  FaPercent, 
  FaCalendar, 
  FaFileAlt,
  FaDownload,
  FaBuilding,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaFileInvoiceDollar,
  FaIdCard
} from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import { catchError, commonModalSetting } from "JS/common";
import {
  addPayment,
  updateAddPayment,
  updateAddPaymentStatus,
  updateAddPaymentPaidAt,
  downloadPrivacyFile,
} from "JS/API";

// 디자인 시스템 임포트
import { designTokens, commonStyles, badgeStyles } from "utils/designSystem";
import { detailModalStyles } from "utils/modalDesignSystem";
import { ERROR_MESSAGE, SERVICE_TYPE, SERVICE_TYPE_ORDER } from "utils/patentDesign";

const addPaymentErrorMessage = {
  requestUserMismatch: "신청자와 결제 대상 회원이 일치하지 않습니다.",
  invalidRequestStatus: ERROR_MESSAGE.invalidRequestStatus,
  serviceTypeRequired: ERROR_MESSAGE.serviceTypeRequired,
  invalidServiceType: ERROR_MESSAGE.invalidServiceType,
};

const PaymentModal = ({
  setPayModal,
  userPk,
  applyPaymentPk,
  onSuccess,
  editData,
  setEditData,
  taxInfo,
  patentDesignRequestPk,
  patentDesignRequestType,
}) => {
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const navigate = useNavigate();

  const [reason, setReason] = useState("");
  const [detailReason, setDetailReason] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const formatNumber = (value) =>
    value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const [surTax, setSurTax] = useState("");
  const totalAmount = (Number(paymentAmount) || 0) + (Number(surTax) || 0);
  const [dueDate, setDueDate] = useState("");
  const [isSurTaxEdited, setIsSurTaxEdited] = useState(false);
  const [paidStatus, setPaidStatus] = useState("");
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [paidAt, setPaidAt] = useState("");
  const [paidAtInput, setPaidAtInput] = useState(""); // 입력용 임시 상태
  const [isPaidAtUpdating, setIsPaidAtUpdating] = useState(false);
  const [paidMethod, setPaidMethod] = useState("");
  const [serviceType, setServiceType] = useState(() =>
    SERVICE_TYPE_ORDER.includes(patentDesignRequestType) ? patentDesignRequestType : ""
  );

  // 결제 상태 옵션
  const paidStatusOptions = [
    { value: "ready", label: "결제 대기", color: "#0369a1", bg: "#e0f2fe" },
    { value: "processing", label: "처리중", color: "#92400e", bg: "#fef3c7" },
    { value: "paid", label: "결제 완료", color: "#166534", bg: "#dcfce7" },
    { value: "failed", label: "결제 실패", color: "#991b1b", bg: "#fef2f2" },
    { value: "canceled", label: "결제 취소", color: "#991b1b", bg: "#fef2f2" },
    { value: "refund_request", label: "환불 요청", color: "#c2410c", bg: "#fff7ed" },
    { value: "refund", label: "환불 완료", color: "#6b21a8", bg: "#f3e8ff" },
  ];

  // 스타일 정의
  const modalStyles = {
    overlay: detailModalStyles.overlay,
    container: detailModalStyles.container,
    header: detailModalStyles.header,
    title: detailModalStyles.title,
    closeButton: detailModalStyles.closeButton,
    content: detailModalStyles.content,
    mainContent: {
      display: 'flex',
      gap: designTokens.spacing.xxl,
      flexDirection: (taxInfo?.tax_invoice_type === "business" || taxInfo?.tax_invoice_type === "private") ? 'row' : 'column'
    },
    paymentSection: {
      flex: (taxInfo?.tax_invoice_type === "business" || taxInfo?.tax_invoice_type === "private") ? 1 : 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: designTokens.spacing.xl
    },
    formField: detailModalStyles.formField,
    formLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      fontSize: '14px',
      fontWeight: '600',
      color: designTokens.colors.text.primary
    },
    formInput: {
      ...commonStyles.input
    },
    totalInput: {
      ...commonStyles.input,
      backgroundColor: designTokens.colors.background,
      color: designTokens.colors.text.primary,
      fontWeight: '700',
      cursor: 'default'
    },
    dateInput: {
      ...commonStyles.input,
      width: '200px'
    },
    taxSection: {
      ...commonStyles.card,
      padding: designTokens.spacing.xl,
      backgroundColor: designTokens.colors.background,
      minWidth: '350px'
    },
    taxHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
      fontSize: '16px',
      fontWeight: '600',
      color: designTokens.colors.text.primary,
      marginBottom: designTokens.spacing.lg,
      padding: `${designTokens.spacing.md} 0`,
      borderBottom: `2px solid ${designTokens.colors.primary}`
    },
    taxInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: designTokens.spacing.md
    },
    taxInfoRow: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      fontSize: '14px',
      color: designTokens.colors.text.secondary
    },
    taxBadge: {
      ...badgeStyles.base,
      ...badgeStyles.primary,
      marginBottom: designTokens.spacing.md
    },
    fileSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: designTokens.spacing.sm,
      padding: designTokens.spacing.md,
      backgroundColor: designTokens.colors.surface,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`
    },
    downloadButton: {
      ...commonStyles.buttonBase,
      backgroundColor: designTokens.colors.secondary,
      color: designTokens.colors.surface,
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      fontSize: '12px',
      width: 'fit-content'
    },
    actionButtons: detailModalStyles.actionButtons,
    primaryButton: detailModalStyles.primaryButton,
    secondaryButton: detailModalStyles.secondaryButton};


  useEffect(() => {
    if (editData) {
      setReason(editData.paid_reason || "");
      setDetailReason(editData.paid_reason_detail || "");
      setPaymentAmount(String(editData.merchant_price || ""));
      setDueDate(editData.ended_at?.split("T")[0] || "");
      setSurTax(String(editData.merchant_vat ?? ""));
      setIsSurTaxEdited(true);
      setPaidStatus(taxInfo?.paid_status || "");
      // 결제일시 포맷 (YYYY-MM-DD HH:mm)
      const formatPaidAt = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const hh = String(date.getHours()).padStart(2, "0");
        const min = String(date.getMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
      };
      setPaidAt(formatPaidAt(taxInfo?.paid_at));
      // datetime-local input용 포맷 (YYYY-MM-DDTHH:mm)
      const formatForInput = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const hh = String(date.getHours()).padStart(2, "0");
        const min = String(date.getMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
      };
      setPaidAtInput(formatForInput(taxInfo?.paid_at));
      setPaidMethod(taxInfo?.paid_method || "");
    } else {
      const today = new Date();
      const nextWeek = new Date(today.setDate(today.getDate() + 7));
      const isoString = nextWeek.toISOString().split("T")[0];
      setDueDate(isoString);
    }
  }, [editData, taxInfo]);

  useEffect(() => {
    if (
      !editData &&
      !isNaN(paymentAmount) &&
      paymentAmount !== "" &&
      !isSurTaxEdited
    ) {
      const amount = parseInt(paymentAmount, 10);
      setSurTax(Math.floor(amount * 0.1).toString());
    }
  }, [paymentAmount, isSurTaxEdited, editData]);

  const handleSubmit = async () => {
    if (!reason.trim() || !paymentAmount || !dueDate || !surTax) {
      setAlertBox({
        mode: "alert",
        context: "항목을 모두 입력해주세요.",
        bool: true,
      });
      return;
    }

    if (patentDesignRequestPk && !editData && !serviceType) {
      commonModalSetting(setAlertBox, true, "alert", "서비스 유형을 선택해주세요.");
      return;
    }

    try {
      const isoDueDate = new Date(dueDate).toISOString();

      const payload = {
        user_pk: userPk,
        merchant_price: Number(paymentAmount),
        merchant_vat: surTax,
        paid_reason: reason,
        paid_reason_detail: detailReason,
        ended_at: isoDueDate,
      };

      if (applyPaymentPk) payload.add_payment_pk = applyPaymentPk;
      if (patentDesignRequestPk) {
        payload.patent_design_request_pk = patentDesignRequestPk;
        payload.service_type = serviceType;
      }

      if (editData?.added_payment_id) {
        payload.added_payment_id = editData.added_payment_id;
      } else {
        await addPayment(payload);
      }

      onSuccess();
      setAlert("closeModal");
      setAlertBox({
        mode: "alert",
        context: editData
          ? "결제 정보가 수정되었습니다."
          : "결제 요청이 완료되었습니다.",
        bool: true,
      });
    } catch (error) {
      const message = addPaymentErrorMessage[error?.response?.data?.detail];
      if (message) {
        commonModalSetting(setAlertBox, true, "alert", message);
        return;
      }
      commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "결제 요청에 실패했습니다. 잠시 후 다시 시도해 주세요."
      );
    }
  };

  const handleDownloadPrivacyFile = async (fileName, originalFileName) => {
    try {
      const blob = await downloadPrivacyFile(fileName, originalFileName);

      if (!(blob instanceof Blob)) {
        throw new Error("Response is not a Blob");
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = originalFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading privacy file:", error);
    }
  };

  const handleEdit = async () => {
    if (!reason.trim() || !paymentAmount || !dueDate || !surTax) {
      setAlertBox({
        mode: "alert",
        context: "항목을 모두 입력해주세요.",
        bool: true,
      });
      return;
    }

    try {
      const isoDueDate = new Date(dueDate).toISOString();

      const payload = {
        merchant_price: Number(paymentAmount),
        merchant_vat: Number(surTax),
        paid_reason: reason,
        paid_reason_detail: detailReason,
        ended_at: isoDueDate,
      };

      const result = await updateAddPayment(editData?.add_payment_pk, payload);
      if (typeof result !== "object") {
        return await catchError(result, navigate, setAlertBox, setAlert);
      }
      if (result.data === false) {
        commonModalSetting(setAlertBox, true, "alert", "결제가 완료된 금액은 수정할 수 없습니다.");
        return;
      }
      onSuccess();
      setAlert("closeModal");
      setAlertBox({
        mode: "alert",
        context: editData
          ? "결제 정보가 수정되었습니다."
          : "결제 요청이 완료되었습니다.",
        bool: true,
      });
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    }
  };

  const handleClose = () => {
    setPayModal(false);
    if (setEditData) {
      setEditData(null);
    }
  };

  // 결제 상태 변경 핸들러
  const handleStatusChange = async (newStatus) => {
    if (!editData?.add_payment_pk || isStatusUpdating) return;

    setIsStatusUpdating(true);
    try {
      const result = await updateAddPaymentStatus(editData.add_payment_pk, newStatus);
      if (typeof result === "object") {
        setPaidStatus(newStatus);

        // 결제완료로 변경 시 현재 일시로 자동 설정 (서버에서 현재 일시로 입력됨)
        if (newStatus === "paid") {
          const now = new Date();
          const yyyy = now.getFullYear();
          const mm = String(now.getMonth() + 1).padStart(2, "0");
          const dd = String(now.getDate()).padStart(2, "0");
          const hh = String(now.getHours()).padStart(2, "0");
          const min = String(now.getMinutes()).padStart(2, "0");
          setPaidAt(`${yyyy}-${mm}-${dd} ${hh}:${min}`);
          setPaidAtInput(`${yyyy}-${mm}-${dd}T${hh}:${min}`);
        }

        setAlertBox({
          mode: "alert",
          context: "결제 상태가 변경되었습니다.",
          bool: true,
        });
        onSuccess(); // 목록 새로고침
      } else {
        return await catchError(result, navigate, setAlertBox, setAlert);
      }
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  // 현재 상태의 스타일 가져오기
  const getCurrentStatusStyle = () => {
    const status = paidStatusOptions.find(opt => opt.value === paidStatus);
    return status || { color: "#6b7280", bg: "#f3f4f6", label: "알 수 없음" };
  };

  // 결제일시 변경 버튼 핸들러 (무통장입금 + 결제완료 상태일 때만)
  const handlePaidAtSubmit = async () => {
    if (!editData?.add_payment_pk || isPaidAtUpdating || !paidAtInput) return;
    if (paidAtInput.replace("T", " ") === paidAt) return; // 변경사항 없으면 무시

    setIsPaidAtUpdating(true);
    try {
      const isoDate = new Date(paidAtInput).toISOString();
      const result = await updateAddPaymentPaidAt(editData.add_payment_pk, isoDate);
      if (typeof result === "object") {
        setPaidAt(paidAtInput.replace("T", " ")); // 표시용 포맷으로 변환
        setAlertBox({
          mode: "alert",
          context: "결제일시가 변경되었습니다.",
          bool: true,
        });
        onSuccess(); // 목록 새로고침
      } else {
        return await catchError(result, navigate, setAlertBox, setAlert);
      }
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsPaidAtUpdating(false);
    }
  };

  // 무통장입금이고 결제완료 상태인지 확인
  const canEditPaidAt = paidMethod === "bank" && paidStatus === "paid";

  return (
    <>
      <div style={modalStyles.overlay}>
        <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div style={modalStyles.header}>
            <h3 style={modalStyles.title}>
              <FaCreditCard />
              추가 결제 요청
            </h3>
            <button
              style={modalStyles.closeButton}
              onClick={handleClose}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.background;
                e.target.style.color = designTokens.colors.text.primary;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = designTokens.colors.text.tertiary;
              }}
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* 컨텐츠 */}
          <div style={modalStyles.content}>
            {patentDesignRequestPk && !editData && (
              <span
                style={{
                  display: 'inline-block',
                  marginBottom: designTokens.spacing.lg,
                  padding: '3px 10px',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: '#e3f6ec',
                  color: '#1e8e57'
                }}
              >
                특허·디자인 신청 No.{patentDesignRequestPk} 연결
              </span>
            )}
            {/* 결제 상태 섹션 - 수정 모드일 때만 표시 */}
            {editData && (
              <div style={{
                marginBottom: designTokens.spacing.xl,
                padding: designTokens.spacing.lg,
                backgroundColor: designTokens.colors.background,
                borderRadius: designTokens.borderRadius.lg,
                border: `1px solid ${designTokens.colors.border}`,
                display: 'flex',
                flexDirection: 'column',
                gap: designTokens.spacing.md
              }}>
                {/* 결제 상태 행 */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: designTokens.spacing.md
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: designTokens.spacing.md
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: designTokens.colors.text.primary,
                      minWidth: '70px'
                    }}>
                      결제 상태:
                    </span>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      backgroundColor: getCurrentStatusStyle().bg,
                      color: getCurrentStatusStyle().color
                    }}>
                      {getCurrentStatusStyle().label}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: designTokens.spacing.sm
                  }}>
                    <span style={{
                      fontSize: '13px',
                      color: designTokens.colors.text.secondary
                    }}>
                      상태 변경:
                    </span>
                    <select
                      value={paidStatus}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={isStatusUpdating}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: `1px solid ${designTokens.colors.border}`,
                        fontSize: '13px',
                        backgroundColor: isStatusUpdating ? '#f3f4f6' : '#ffffff',
                        cursor: isStatusUpdating ? 'not-allowed' : 'pointer',
                        minWidth: '130px'
                      }}
                    >
                      {paidStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* 결제일 행 */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: designTokens.spacing.md,
                  paddingTop: designTokens.spacing.md,
                  borderTop: `1px solid ${designTokens.colors.borderLight}`
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: designTokens.spacing.md
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: designTokens.colors.text.primary,
                      minWidth: '70px'
                    }}>
                      결제일시:
                    </span>
                    <span style={{
                      fontSize: '13px',
                      color: paidAt ? designTokens.colors.text.primary : designTokens.colors.text.tertiary
                    }}>
                      {paidAt || "-"}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      color: paidMethod === "bank" ? '#0369a1' : '#6b21a8',
                      backgroundColor: paidMethod === "bank" ? '#e0f2fe' : '#f3e8ff',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {paidMethod === "bank" ? "무통장입금" : "TOSS"}
                    </span>
                  </div>
                  {/* 무통장입금일 때만 수정 가능 */}
                  {paidMethod === "bank" && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: designTokens.spacing.sm
                    }}>
                      <input
                        type="datetime-local"
                        value={paidAtInput}
                        onChange={(e) => setPaidAtInput(e.target.value)}
                        disabled={!canEditPaidAt || isPaidAtUpdating}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${designTokens.colors.border}`,
                          fontSize: '13px',
                          backgroundColor: '#ffffff',
                          cursor: 'pointer',
                          minWidth: '180px',
                          opacity: (!canEditPaidAt || isPaidAtUpdating) ? 0.5 : 1
                        }}
                      />
                      <button
                        onClick={handlePaidAtSubmit}
                        disabled={!canEditPaidAt || isPaidAtUpdating || paidAtInput.replace("T", " ") === paidAt || !paidAtInput}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: '500',
                          backgroundColor: (paidAtInput.replace("T", " ") === paidAt || !paidAtInput) ? '#e5e7eb' : designTokens.colors.primary,
                          color: (paidAtInput.replace("T", " ") === paidAt || !paidAtInput) ? '#9ca3af' : '#ffffff',
                          cursor: (isPaidAtUpdating || paidAtInput.replace("T", " ") === paidAt || !paidAtInput) ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                          opacity: !canEditPaidAt ? 0.5 : 1
                        }}
                      >
                        {isPaidAtUpdating ? '변경 중...' : '변경'}
                      </button>
                      {!canEditPaidAt && paidStatus !== "paid" && (
                        <span style={{
                          fontSize: '11px',
                          color: '#f59e0b',
                          whiteSpace: 'nowrap'
                        }}>
                          결제완료 상태에서만 변경 가능
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={modalStyles.mainContent}>
              {/* 결제 정보 섹션 */}
              <div style={modalStyles.paymentSection}>
                {patentDesignRequestPk && !editData && (
                  <div style={modalStyles.formField}>
                    <label htmlFor="payment-service-type" style={modalStyles.formLabel}>
                      <FaFileAlt />
                      서비스 유형 *
                    </label>
                    <select
                      id="payment-service-type"
                      style={modalStyles.formInput}
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value)}
                    >
                      <option value="">선택해 주세요</option>
                      {SERVICE_TYPE_ORDER.map((type) => (
                        <option key={type} value={type}>
                          {SERVICE_TYPE[type]}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={modalStyles.formField}>
                  <label style={modalStyles.formLabel}>
                    <FaFileAlt />
                    추가 결제 사유 *
                  </label>
                  <input
                    style={modalStyles.formInput}
                    placeholder="추가 결제 사유를 입력해주세요."
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>

                <div style={modalStyles.formField}>
                  <label style={modalStyles.formLabel}>
                    <FaFileAlt />
                    추가 결제 상세 사유
                  </label>
                  <input
                    style={modalStyles.formInput}
                    placeholder="추가 결제 상세 사유를 입력해주세요."
                    type="text"
                    value={detailReason}
                    onChange={(e) => setDetailReason(e.target.value)}
                  />
                </div>

                <div style={modalStyles.formField}>
                  <label style={modalStyles.formLabel}>
                    <FaDollarSign />
                    추가 결제 금액 *
                  </label>
                  <input
                    style={modalStyles.formInput}
                    placeholder="ex) 100,000"
                    type="text"
                    value={formatNumber(paymentAmount)}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, "");
                      if (!isNaN(rawValue)) {
                        setPaymentAmount(rawValue);
                      }
                    }}
                  />
                </div>

                <div style={modalStyles.formField}>
                  <label style={modalStyles.formLabel}>
                    <FaPercent />
                    부가세 *
                  </label>
                  <input
                    style={modalStyles.formInput}
                    placeholder="자동 계산 또는 직접 입력"
                    type="text"
                    value={surTax !== "" ? formatNumber(surTax) : ""}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/,/g, "");
                      if (!isNaN(rawValue)) {
                        setSurTax(rawValue);
                        setIsSurTaxEdited(true);
                      }
                    }}
                  />
                  <div style={{
                    fontSize: '12px',
                    color: designTokens.colors.text.tertiary,
                    marginTop: designTokens.spacing.xs
                  }}>
                    결제 금액의 10%가 자동으로 계산됩니다.
                  </div>
                </div>

                <div style={modalStyles.formField}>
                  <label style={modalStyles.formLabel}>
                    <FaFileInvoiceDollar />
                    최종 결제 금액
                  </label>
                  <input
                    style={modalStyles.totalInput}
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={`${formatNumber(totalAmount)}원`}
                  />
                  <div style={{
                    fontSize: '12px',
                    color: designTokens.colors.text.tertiary,
                    marginTop: designTokens.spacing.xs
                  }}>
                    추가 결제 금액 + 부가세가 자동으로 합산됩니다.
                  </div>
                </div>

                <div style={modalStyles.formField}>
                  <label style={modalStyles.formLabel}>
                    <FaCalendar />
                    결제 마감일 *
                  </label>
                  <input
                    style={modalStyles.dateInput}
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              </div>

              {/* 세금계산서 정보 섹션 */}
              {(taxInfo?.tax_invoice_type === "business" || taxInfo?.tax_invoice_type === "private") && (
                <div style={modalStyles.taxSection}>
                  <h3 style={modalStyles.taxHeader}>
                    <FaFileInvoiceDollar />
                    세금계산서 정보
                  </h3>

                  <div style={modalStyles.taxBadge}>
                    {taxInfo?.tax_invoice_type === "private" ? (
                      <>
                        <FaUser size={10} />
                        개인 발행
                      </>
                    ) : (
                      <>
                        <FaBuilding size={10} />
                        사업자 발행
                      </>
                    )}
                  </div>

                  <div style={modalStyles.taxInfo}>
                    {taxInfo?.tax_invoice_type === "private" && (
                      <div style={modalStyles.taxInfoRow}>
                        <FaPhone size={12} />
                        <strong>핸드폰 번호:</strong>
                        <span>{taxInfo?.phone || "-"}</span>
                      </div>
                    )}

                    {taxInfo?.tax_invoice_type === "business" && (
                      <>
                        <div style={modalStyles.taxInfoRow}>
                          <FaIdCard size={12} />
                          <strong>사업자등록번호:</strong>
                          <span>{taxInfo.biz_regist_number || "-"}</span>
                        </div>
                        
                        <div style={modalStyles.taxInfoRow}>
                          <FaBuilding size={12} />
                          <strong>회사명:</strong>
                          <span>{taxInfo.company || "-"}</span>
                        </div>
                        
                        <div style={modalStyles.taxInfoRow}>
                          <FaUser size={12} />
                          <strong>소유주명:</strong>
                          <span>{taxInfo?.owner || "-"}</span>
                        </div>
                        
                        <div style={modalStyles.taxInfoRow}>
                          <FaEnvelope size={12} />
                          <strong>이메일:</strong>
                          <span>{taxInfo.email || "-"}</span>
                        </div>

                        <div style={modalStyles.fileSection}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: designTokens.spacing.sm,
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            <FaFileAlt size={12} />
                            사업자 등록증
                          </div>
                          
                          <div style={{
                            fontSize: '13px',
                            color: designTokens.colors.text.secondary,
                            marginBottom: designTokens.spacing.sm
                          }}>
                            {taxInfo.biz_license_original_filename || "파일 없음"}
                          </div>
                          
                          {taxInfo.biz_license_filename && (
                            <button
                              style={modalStyles.downloadButton}
                              onClick={() =>
                                handleDownloadPrivacyFile(
                                  taxInfo.biz_license_filename,
                                  taxInfo.biz_license_original_filename
                                )
                              }
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = designTokens.colors.secondaryHover || '#4b5563';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = designTokens.colors.secondary;
                              }}
                            >
                              <FaDownload size={10} />
                              다운로드
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 액션 버튼 */}
          <div style={modalStyles.actionButtons}>
            <button
              style={modalStyles.primaryButton}
              onClick={editData ? handleEdit : handleSubmit}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.primaryHover || '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = designTokens.colors.primary;
              }}
            >
              {editData ? (
                <>
                  <FaEdit size={12} />
                  수정
                </>
              ) : (
                <>
                  <FaPlus size={12} />
                  생성
                </>
              )}
            </button>
            <button
              style={modalStyles.secondaryButton}
              onClick={handleClose}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = designTokens.colors.background;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = designTokens.colors.surface;
              }}
            >
              취소
            </button>
          </div>
        </div>
      </div>

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "closeModal") setPayModal(false);
            if (alert === "logout") navigate("/");
          }}
        />
      )}
    </>
  );
};

export default PaymentModal;